import { useState, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { mapOfferToDestination, DestinationUI } from "@/adapters/offerAdapter";
import OfferCardSkeleton from "@/components/offers/OfferCardSkeleton";
import { Offer } from "@/types/offer";
import { URL_FLIGHTS } from "@/constants/EngineConstants";
import unmissableOffersJson from "@/data/unmissable_offers.json";
import { isOfferValid } from "@/utils/offerDate.util";

const SKELETON_COUNT = 4;

interface UnmissableOfferCardProps {
  destination: DestinationUI;
  index: number;
}

const FALLBACK_IMAGES = [
  "https://s3.amazonaws.com/media.costamar.com/IMG-WEB/DEFAULT04.jpg",
  "https://s3.amazonaws.com/media.costamar.com/IMG-WEB/DEFAULT06.jpg",
];

const getRandomFallbackImage = () =>
  FALLBACK_IMAGES[Math.floor(Math.random() * FALLBACK_IMAGES.length)];

const UnmissableOfferCard = ({ destination, index }: UnmissableOfferCardProps) => {
  const [imgSrc, setImgSrc] = useState(destination.image);

  const handleImageError = () => {
    setImgSrc(getRandomFallbackImage());
  };

  return (
    <a
      href={destination.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        window.gtag?.("event", "click_open_blank", {
          link_url: destination.url,
          link_text: "ofertas_imperdibles",
          link_domain: new URL(destination.url).hostname
        });
      }}
      className="relative h-96 rounded-2xl overflow-hidden group cursor-pointer animate-fade-in block flex flex-col"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {/* Image Section */}
      <div className="relative flex-1">
        <img
          src={imgSrc}
          alt={`Vista de ${destination.city}, ${destination.country}`}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={handleImageError}
        />
        {/* Subtle gradient for image depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30" />
      </div>

      {/* Footer with gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary via-primary/90 to-transparent p-4 pt-12">
        <div className="flex items-end justify-between">
          {/* Left - Destination */}
          <div className="mb-3">
            <h3 className="text-2xl font-bold text-white">
              {destination.city}
            </h3>
            <p className="text-sm font-normal text-white/80">
              {destination.country}
            </p>
          </div>
          
          {/* Right - Origin, Price, Flight Type */}
          <div className="text-right">
            <p className="text-sm text-white/80">
              Desde
            </p>
            <p className="text-3xl font-bold text-white mb-1">
              ${destination.priceUSD.toFixed(2)}
            </p>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide bg-secondary text-white">
              {destination.flightIndicator}
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

const UnmissableOffersSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  
  const [offers, setOffers] = useState<Offer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  const destinations = useMemo(() => {
    return offers?.map(mapOfferToDestination) ?? [];
  }, [offers]);

  useEffect(() => {
    let isMounted = true;

    const fetchOffers = async () => {
      try {
        setIsLoading(true);
        
        const data: Offer[] = unmissableOffersJson
          .filter(x => isOfferValid(x.url))
          .map(x => ({
            ...x,
            url: `https://${URL_FLIGHTS}/${x.originId}/${x.destinationId}/${x.url}/1/0/0`,
            imageUrl: `https://s3.amazonaws.com/media.costamar.com/IMG-WEB/${x.destinationId}.jpg`
        })) as Offer[];

        if (isMounted) {
          setOffers(data);
        }
      } catch (error) {
        setError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!api) return;

    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateScrollState();
    api.on("select", updateScrollState);
    api.on("reInit", updateScrollState);

    return () => {
      api.off("select", updateScrollState);
    };
  }, [api]);
  

  if (error) {
    return null;
  }

  return (
    <section className="py-16">
      {/* py-16 bg-muted/30 */}
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-3xl lg:text-4xl text-foreground italic mb-2">
            Ofertas Imperdibles
          </h2>
          <p className="text-muted-foreground">
            Los mejores precios para tu próximo destino
          </p>
        </div>

        {/* Carousel */}
        <div className="relative group/carousel">
          {/* Navigation Arrows */}
          <button
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            className={cn(
              "absolute -left-4 md:-left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl bg-primary",
              canScrollPrev
                ? "hover:opacity-90 hover:scale-105 cursor-pointer"
                : "opacity-40 cursor-not-allowed"
            )}
            aria-label="Anterior"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            className={cn(
              "absolute -right-4 md:-right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl bg-primary",
              canScrollNext
                ? "hover:opacity-90 hover:scale-105 cursor-pointer"
                : "opacity-40 cursor-not-allowed"
            )}
            aria-label="Siguiente"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {isLoading
                ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <CarouselItem
                      key={`skeleton-${index}`}
                      className="pl-4 basis-[85%] sm:basis-[45%] lg:basis-[30%]"
                    >
                      <OfferCardSkeleton />
                    </CarouselItem>
                  ))
                : destinations.map((destination, index) => (
                    <CarouselItem
                      key={destination.id}
                      className="pl-4 basis-[85%] sm:basis-[45%] lg:basis-[30%]"
                    >
                      <UnmissableOfferCard destination={destination} index={index} />
                    </CarouselItem>
                  ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

export default UnmissableOffersSection;
